import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBaskets } from '../context/BasketContext';

export default function AddToBasketButton({ productId }) {
  const { isAuthenticated } = useAuth();
  const { userBaskets, addToBasket, createBasket } = useBaskets();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState('');
  const dropdownRef = useRef(null);
  const feedbackTimer = useRef(null);

  useEffect(() => {
    return () => { if (feedbackTimer.current) clearTimeout(feedbackTimer.current); };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const showFeedback = (msg) => {
    setFeedback(msg);
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    feedbackTimer.current = setTimeout(() => setFeedback(''), 2500);
  };

  const handleAdd = async (e, basketId) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    const success = await addToBasket(basketId, productId, 1);
    if (success) {
      showFeedback('Added!');
      setOpen(false);
    } else {
      showFeedback('Failed');
    }
    setLoading(false);
  };

  const handleQuickCreate = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    const name = prompt('New basket name:');
    if (name && name.trim()) {
      const basket = await createBasket(name.trim(), '');
      if (basket) {
        const success = await addToBasket(basket.id, productId, 1);
        if (success) {
          showFeedback('Created & added!');
          setOpen(false);
        }
      }
    }
    setLoading(false);
  };

  if (!isAuthenticated) {
    return (
      <Link
        to="/login"
        onClick={(e) => e.stopPropagation()}
        className="mt-1 w-full block text-center text-[10px] text-gray-400 hover:text-blue-600 py-1"
      >
        Login to add to basket
      </Link>
    );
  }

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(!open); }}
        disabled={loading}
        className="w-full mt-1 text-[10px] font-medium text-gray-500 hover:text-blue-600 py-1 transition flex items-center justify-center gap-1"
      >
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        {feedback || 'Add to Basket'}
      </button>

      {open && (
        <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 max-h-48 overflow-y-auto">
          {userBaskets.length > 0 ? (
            userBaskets.map(b => (
              <button
                key={b.id}
                onClick={(e) => handleAdd(e, b.id)}
                disabled={loading}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 transition disabled:opacity-50 truncate"
              >
                {b.name}
                <span className="text-gray-400 ml-1">({b.items_count || 0})</span>
              </button>
            ))
          ) : (
            <p className="text-xs text-gray-400 px-3 py-1.5">No baskets yet</p>
          )}
          <div className="border-t mt-1 pt-1">
            <button
              onClick={handleQuickCreate}
              disabled={loading}
              className="w-full text-left px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 transition font-medium"
            >
              + New Basket
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
